import type { ReactNode } from "react";
import { cn } from "@/lib/utils";

// Section label used above blocks in detail panels (mono, uppercase, 10px).
// Only swap occurrences that use EXACTLY this class — leave those with extra
// modifiers (e.g. mt-*, flex) in place.
export function SectionLabel({ children }: { children: ReactNode }) {
  return (
    <div className="font-mono text-[10px] font-bold uppercase tracking-wider text-text-secondary">
      {children}
    </div>
  );
}

// Numbered section header used in multi-step modal forms.
export function SectionHeader({ num, children }: { num: number; children: React.ReactNode }) {
  return (
    <div className="flex items-center gap-2 text-[11px] font-semibold tracking-[0.06em] uppercase text-muted-foreground">
      <span className="flex h-[18px] w-[18px] items-center justify-center rounded-full bg-secondary text-[10px] font-semibold text-foreground">
        {num}
      </span>
      {children}
    </div>
  );
}

// Toggle/radio chip used in preference and occasion selectors.
// gap-2 is included permanently — it is a no-op for single-child chips and
// required when the chip renders an icon + label (e.g. feature chips).
export function Chip({
  selected,
  onClick,
  children,
  asRadio = false,
}: {
  selected: boolean;
  onClick: () => void;
  children: React.ReactNode;
  asRadio?: boolean;
}) {
  const radioRole = asRadio ? "radio" : undefined;
  return (
    <button
      type="button"
      role={radioRole}
      aria-pressed={asRadio ? undefined : selected}
      aria-checked={asRadio ? selected : undefined}
      onClick={onClick}
      className={cn(
        "inline-flex h-11 sm:h-9 items-center whitespace-nowrap rounded-full border px-4 sm:px-3.5 text-base sm:text-sm font-medium transition-all gap-2",
        selected
          ? "border-primary bg-primary text-primary-foreground"
          : "border-border bg-background text-foreground hover:border-muted-foreground/30 hover:bg-accent",
      )}
    >
      {children}
    </button>
  );
}
